import { useEffect, useState } from 'react';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { paymentMethodsAPI } from '../lib/api';
import { toast } from 'sonner';

const empty = {
  label: '',
  type: 'upi',
  upi_id: '',
  account_name: '',
  account_number: '',
  ifsc: '',
  bank_name: '',
  instructions: '',
  qr_url: '',
  is_active: true,
  sort_order: 0,
};

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await paymentMethodsAPI.getAll();
      setMethods(res.data.methods || []);
    } catch {
      toast.error('Failed to load payment methods');
    }
  };

  useEffect(() => { load(); }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    try {
      setSaving(true);
      const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
      if (editing) await paymentMethodsAPI.update(editing, payload);
      else await paymentMethodsAPI.create(payload);
      toast.success(editing ? 'Payment method updated' : 'Payment method added. Players will see it on Add money.');
      setForm(empty);
      setEditing(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save payment method');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (method) => {
    setEditing(method.id);
    setForm({
      label: method.label || '',
      type: method.type || 'upi',
      upi_id: method.upi_id || '',
      account_name: method.account_name || '',
      account_number: method.account_number || '',
      ifsc: method.ifsc || '',
      bank_name: method.bank_name || '',
      instructions: method.instructions || '',
      qr_url: method.qr_url || '',
      is_active: method.is_active !== false,
      sort_order: method.sort_order || 0,
    });
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this payment method from the app?')) return;
    try {
      await paymentMethodsAPI.remove(id);
      toast.success('Removed');
      if (editing === id) {
        setEditing(null);
        setForm(empty);
      }
      load();
    } catch {
      toast.error('Could not remove');
    }
  };

  return (
    <div data-testid="payments-page">
      <div className="page-header">
        <h1 className="page-title">Payment Methods</h1>
        <p className="page-subtitle">These accounts appear on Add money in the app. Players pay here, then upload a screenshot.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="stat-card space-y-4">
          <h2 className="font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4" /> {editing ? 'Edit method' : 'Add method'}
          </h2>
          <div className="space-y-2">
            <Label>Name shown in the app</Label>
            <Input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="Google Pay / Company UPI" />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set('type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank">Bank transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.type === 'upi' ? (
            <div className="space-y-2">
              <Label>UPI ID</Label>
              <Input value={form.upi_id} onChange={(e) => set('upi_id', e.target.value)} placeholder="name@ybl" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Account name</Label>
                <Input value={form.account_name} onChange={(e) => set('account_name', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Account number</Label>
                <Input value={form.account_number} onChange={(e) => set('account_number', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>IFSC</Label>
                <Input value={form.ifsc} onChange={(e) => set('ifsc', e.target.value)} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Bank name</Label>
                <Input value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label>QR image URL (optional)</Label>
            <Input value={form.qr_url} onChange={(e) => set('qr_url', e.target.value)} placeholder="https://..." />
          </div>
          <div className="space-y-2">
            <Label>Instructions for the player</Label>
            <Textarea value={form.instructions} onChange={(e) => set('instructions', e.target.value)} rows={3} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Show in the app</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Add method'}</Button>
            {editing && (
              <Button variant="outline" onClick={() => { setEditing(null); setForm(empty); }}>Cancel</Button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {methods.length === 0 ? (
            <div className="stat-card text-sm text-muted-foreground">No methods yet. Until you add one, the app falls back to the default UPI ID.</div>
          ) : methods.map((method) => (
            <div key={method.id} className="stat-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-primary" />
                    {method.label}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1 capitalize">{method.type} · {method.is_active ? 'Visible in app' : 'Hidden'}</p>
                  <p className="font-mono text-sm mt-2">
                    {method.type === 'bank'
                      ? `${method.account_name || ''} ${method.account_number || ''} ${method.ifsc || ''}`
                      : method.upi_id}
                  </p>
                  {method.instructions && <p className="text-xs text-muted-foreground mt-2">{method.instructions}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(method)}>Edit</Button>
                  <Button size="sm" variant="outline" className="text-red-400" onClick={() => remove(method.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
